import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useState
} from "react"
import { Item, prepareItems } from "../shared/assets"
import { defaultItems, getSavedItems } from "../shared/storage"
import { Breakpoint, Tab } from "../shared/app"

const AppContext = createContext<{
    showWatermark: boolean
    setShowWatermark: (showWatermark: boolean) => void
    items: Item[]
    setItems: (items: Item[]) => void
    activeTab: Tab
    setActiveTab: (activeTab: Tab) => void
    activeItemKey: number | null
    setActiveItemKey: (activeItemKey: number | null) => void
    enableSearch: boolean
    setEnableSearch: (enableSearch: boolean) => void
    breakpoint: Breakpoint
}>({
    showWatermark: true,
    setShowWatermark: () => {},
    items: [],
    setItems: () => {},
    activeTab: "info",
    setActiveTab: () => {},
    activeItemKey: null,
    setActiveItemKey: () => {},
    enableSearch: false,
    setEnableSearch: () => {},
    breakpoint: null
})

export interface AppProviderProps {
    children: ReactNode
}

export const AppProvider = ({ children }: AppProviderProps) => {
    const [showWatermark, setShowWatermark] = useState(true)
    const [items, setItems] = useState<Item[]>(
        prepareItems(getSavedItems() || defaultItems)
    )
    const [activeTab, setActiveTab] = useState<Tab>("info")
    const [activeItemKey, setActiveItemKey] = useState<number | null>(null)

    const [enableSearch, setEnableSearch] = useState<boolean>(
        localStorage.getItem("enableSearch") !== null
    )

    const [breakpoint, setBreakpoint] = useState<Breakpoint>(null)

    const initialBreakpoint = () => {
        if (window.innerWidth >= 992) {
            setBreakpoint("lg")
        } else {
            setBreakpoint(null)
        }
    }

    useEffect(() => {
        initialBreakpoint()
        window.addEventListener("resize", initialBreakpoint)
        return () => window.removeEventListener("resize", initialBreakpoint)
    }, [])

    return (
        <AppContext.Provider
            value={{
                showWatermark,
                setShowWatermark,
                items,
                setItems,
                activeTab,
                setActiveTab,
                activeItemKey,
                setActiveItemKey,
                enableSearch,
                setEnableSearch,
                breakpoint
            }}
        >
            {children}
        </AppContext.Provider>
    )
}

export const useApp = () => {
    const context = useContext(AppContext)
    if (context === undefined) {
        throw new Error("useApp must be used within a AppProvider")
    }
    return context
}
